import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { dashboardApi } from '../lib/api';
import { useAuthStore } from './auth-store';
import { useStoreStore } from './store-store';

export interface DashboardStats {
  status_counts: { [key: string]: number; };
  total_items: number;
  marketplaces: Array<{
    code: string;
    title: string;
    value: string;
    image?: string;
  }>;
  filters: {
    include_test_order: boolean;
    marketplace_filter: string[];
  };
}

export interface DetailedStats {
  order_processing: {
    outstanding: number;
    payment_verified: number;
    pending_completion: number;
    item_completion_verified: number;
  };
  procurement: {
    procured_items: number;
    unsettled_items: number;
  };
  cancelled: {
    under_review: number;
    refunded: number;
    not_eligible_refund: number;
  };
  marketplace_shipping: {
    on_its_way: number;
    delayed: number;
    unreceived: number;
  };
  facility: {
    not_delivered_to_facility: number;
    delivered_to_facility: number;
    shipped_to_warehouse: number;
  };
  after_shipment: {
    received_at_warehouse: number;
    received_at_distribution: number;
    shipped_to_distribution: number;
    delivered_to_customer: number;
  };
}

interface DashboardState {
  stats: DashboardStats | null;
  detailedStats: DetailedStats | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    includeTestOrder: boolean;
    marketplaceFilter: string[];
  };
}

interface DashboardActions {
  fetchStats: () => Promise<void>;
  fetchDetailedStats: () => Promise<void>;
  updateFilters: (filters: Partial<DashboardState['filters']>) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export type DashboardStore = DashboardState & DashboardActions;

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set, get) => ({
      // Initial state
      stats: null,
      detailedStats: null,
      isLoading: false,
      error: null,
      filters: {
        includeTestOrder: false,
        marketplaceFilter: [],
      },

      // Actions
      fetchStats: async () => {
        const { filters } = get();
        set({ isLoading: true, error: null });

        try {
          // Get token from auth store
          const authState = useAuthStore.getState();
          if (!authState.token) {
            throw new Error('No authentication token available');
          }

          // Get current store from store store
          const storeState = useStoreStore.getState();

          if (!storeState.currentStore) {
            throw new Error('No store selected');
          }

          const response = await dashboardApi.getOverview(authState.token, storeState.currentStore.id, {
            include_test_order: filters.includeTestOrder,
            marketplace_filter: filters.marketplaceFilter,
          });

          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch dashboard stats');
          }

          // Transform statistics response to match the expected DashboardStats format
          const statsData = response.data;
          const transformedStats = {
            status_counts: {
              // Order Processing
              outstanding: statsData.order_processing?.outstanding || 0,
              'payment-verified': statsData.order_processing?.payment_verified || 0,
              'pending-item-completion': statsData.order_processing?.pending_completion || 0,
              'item-completion-payment-unverified': statsData.order_processing?.item_completion_unverified || 0,
              'item-completion-payment-verified': statsData.order_processing?.item_completion_verified || 0,
              'prepare-for-procurement': statsData.order_processing?.ready_for_procurement || 0,
              // Procurement
              'procured-items': statsData.procurement?.procured_items || 0,
              'unsettled-items': statsData.procurement?.unsettled_items || 0,
              // Cancelled
              'under-review': statsData.cancelled?.under_review || 0,
              refunded: statsData.cancelled?.refunded || 0,
              'not-eligilbe-refund': statsData.cancelled?.not_eligible_refund || 0,
              'partial-fulfillment': statsData.cancelled?.partial_fulfillment || 0,
              // Marketplace Shipping
              'on-its-way': statsData.marketplace_shipping?.on_its_way || 0,
              delayed: statsData.marketplace_shipping?.delayed || 0,
              unreceived: statsData.marketplace_shipping?.unreceived || 0,
              // Facility
              'procured-but-not-delivered-to-facility': statsData.facility?.not_delivered_to_facility || 0,
              'delivered-to-facility': statsData.facility?.delivered_to_facility || 0,
              'shipped-to-destination-warehouse': statsData.facility?.shipped_to_warehouse || 0,
              // After Shipment
              'received-at-main-warehouse': statsData.after_shipment?.received_at_warehouse || 0,
              'received-at-distribution-center': statsData.after_shipment?.received_at_distribution || 0,
              'shipped-to-distribution-center': statsData.after_shipment?.shipped_to_distribution || 0,
              'delivered-to-the-customer': statsData.after_shipment?.delivered_to_customer || 0,
            },
            total_items: statsData.total_items || 0,
            marketplaces: statsData.marketplaces || [],
            current_store: statsData.current_store,
            filters: statsData.filters,
          };

          set({
            stats: transformedStats,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Dashboard fetch error:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard stats';
          set({
            isLoading: false,
            error: `API Error: ${errorMessage}`,
          });
        }
      },

      fetchDetailedStats: async () => {
        const { filters } = get();
        set({ isLoading: true, error: null });

        try {
          // Get token from auth store
          const authState = useAuthStore.getState();
          if (!authState.token) {
            throw new Error('No authentication token available');
          }

          // Get current store from store store
          const storeState = useStoreStore.getState();
          if (!storeState.currentStore) {
            throw new Error('No store selected');
          }

          const response = await dashboardApi.getStatistics(authState.token, storeState.currentStore.id, filters.includeTestOrder);

          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch detailed stats');
          }

          set({
            detailedStats: response.data,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch detailed stats';
          set({
            isLoading: false,
            error: errorMessage,
          });
        }
      },

      updateFilters: (newFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        }));
        
        // Auto-fetch stats when filters change
        get().fetchStats();
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'dashboard-storage',
      partialize: (state) => ({
        filters: state.filters,
        stats: state.stats,
      }),
    }
  )
);
